import { Button, DeleteIcon, GridColumn, GridRow, GroupPicker, Select, SelectOptionType } from '@bp/ui-components';
import { useFormikContext } from 'formik';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { MultiValue, SingleValue } from 'react-select';
import { useCreateSelectOptions } from '../../../../hooks/useCreateSelectOptions';
import { useGradeOptions } from '../../../../hooks/useGradeOptions';
import { useMemorizedCacheTag } from '../../../../hooks/useMemorizedCacheTag';
import { useDefaultDivisionsQuery, useGradeGroupsListQuery } from '../../../../types/planung-graphql-client-defs';
import { CurriculumClassType, CurriculumFormType } from '../../types';

type CurriculumClassesFormItemProps = {
  index: number;
  multiEnabled: boolean;
  curriculumClass: CurriculumClassType;
  disabled: boolean;
  enableGroups: boolean;
  selectableGrades: number[];
  onDelete: () => void;
};

export const CurriculumClassesFormItem = ({
  index,
  multiEnabled,
  curriculumClass,
  disabled,
  enableGroups,
  selectableGrades,
  onDelete,
}: CurriculumClassesFormItemProps) => {
  const { t } = useTranslation();
  const gradeGroupsContext = useMemorizedCacheTag('GRADE_GROUPS');
  const divisionsContext = useMemorizedCacheTag('DIVISIONS');

  const formik = useFormikContext<CurriculumFormType>();

  const [{ data: gradeGroupsData }] = useGradeGroupsListQuery({
    context: gradeGroupsContext,
  });

  const [{ data: divisionsData }] = useDefaultDivisionsQuery({ context: divisionsContext });

  const options =
    useMemo(() => {
      return divisionsData?.defaultDivisions.map((division) => {
        return {
          options: division.groups.map((group) => {
            return { value: group.uuid, label: group.shortName };
          }),
        };
      });
    }, [divisionsData?.defaultDivisions]) ?? [];

  const gradeOptions = useGradeOptions(selectableGrades);

  const gradeGroupsOptions = useCreateSelectOptions(gradeGroupsData?.gradeGroups, 'uuid', 'shortName');

  return (
    <GridRow spacingBottom='s' spacingTop='none'>
      <GridColumn width={10}>
        <GridRow spacingBottom='none' spacingTop='none'>
          <GridColumn width={6}>
            <Select
              onChange={async (option) => {
                const multiValue = option as MultiValue<SelectOptionType>;
                const singleValue = option as SingleValue<SelectOptionType>;
                await formik.setFieldTouched(`curriculumGrades.${index}.grades`, true);
                await formik.setFieldValue(
                  `curriculumGrades.${index}.grades`,
                  multiEnabled
                    ? multiValue.map((value) => {
                        return value.value;
                      })
                    : [singleValue?.value ?? 0],
                );
              }}
              label={t('classes.level.full')}
              options={gradeOptions}
              isSearchable
              isMulti={multiEnabled}
              name={`curriculumGrades.${index}.grades`}
              tooltipText={t('curriculum.multiplyHint')}
              defaultValue={
                curriculumClass.grades
                  ? curriculumClass.grades.map((g) => {
                      return {
                        value: g,
                        label: '' + g,
                      };
                    })
                  : undefined
              }
            />
          </GridColumn>
          <GridColumn width={6}>
            <Select
              onChange={async (option) => {
                const multiValue = option as MultiValue<SelectOptionType>;
                const singleValue = option as SingleValue<SelectOptionType>;
                await formik.setFieldTouched(`curriculumGrades.${index}.gradeGroups`, true);
                const value = multiEnabled
                  ? multiValue.map((value) => {
                      return { uuid: value.value };
                    })
                  : singleValue
                    ? [{ uuid: singleValue?.value }]
                    : null;

                await formik.setFieldValue(`curriculumGrades.${index}.gradeGroups`, value);
              }}
              defaultValue={
                curriculumClass.gradeGroups
                  ? curriculumClass.gradeGroups.map((gg) => {
                      return {
                        value: gg.uuid,
                        label: gg.name ?? '',
                      };
                    })
                  : undefined
              }
              options={gradeGroupsOptions}
              label={t('gradeGroups.title', { count: 2 })}
              tooltipText={t('curriculum.multiplyHint')}
              name={`curriculumGrades.${index}.gradeGroups`}
              isSearchable
              isClearable
              isMulti={multiEnabled}
            />
          </GridColumn>
        </GridRow>
        {enableGroups && (
          <GridRow spacingBottom='none' spacingTop='none'>
            <div style={{ width: '100%' }}>
              <GroupPicker
                dimUnmatched={true}
                readonly={disabled}
                name={`curriculumGrades.${index}.groups`}
                label={t('common.group_other')}
                disabled={disabled}
                tooltipText={t('curriculum.multiplyHint')}
                options={options}
                onChange={async (multiValue) => {
                  await formik.setFieldTouched(`curriculumGrades.${index}.groups`, true);
                  await formik.setFieldValue(
                    `curriculumGrades.${index}.groups`,
                    multiValue.map((value) => {
                      return { uuid: value.value, name: value.label };
                    }),
                  );
                }}
                defaultValue={
                  curriculumClass.groups
                    ? curriculumClass.groups.map((group) => {
                        return {
                          value: group.uuid,
                          label: group.name ?? '',
                        };
                      })
                    : undefined
                }
                dropdownHeader={t('divisions.default')}
              />
            </div>
          </GridRow>
        )}
      </GridColumn>

      <GridColumn width={2} className='mt-5' align='end'>
        <Button hierarchy='tertiary' disabled={disabled} onClick={onDelete} icon={<DeleteIcon />} />
      </GridColumn>
    </GridRow>
  );
};
